import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../components/app/Header";
import Footer from "../components/app/Footer";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import {
  Sparkles, Check, Crown, Loader2, MessageCircle, CloudRain, Tag, Camera,
  Calendar, Award, MapPin, Bell, Users
} from "lucide-react";

export default function Pro() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [billing, setBilling] = useState({ membership: "free", trial_used: false });
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();

  useEffect(() => {
    if (params.get("canceled")) toast.info(t("pro.toastCanceled"));
  }, [params, t]);

  useEffect(() => {
    if (!user) return;
    api.get("/billing/me").then(({ data }) => setBilling(data)).catch(() => {});
  }, [user]);

  const isPro = !!(user?.is_pro || billing?.membership === "pro");
  const trialAvailable = !billing?.trial_used && !isPro;

  const goPro = async () => {
    if (!user) { toast.info(t("pro.toastNeedLogin")); window.location.href = "/login"; return; }
    setBusy(true);
    try {
      // iter101 — fixed endpoint name (was `/billing/checkout-session`, which
      // never existed) + correct payload shape expected by routers/billing.py.
      const { data } = await api.post("/billing/checkout", {
        package_id: "pro_monthly",
        origin_url: window.location.origin,
      });
      if (data?.url) window.location.href = data.url;
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  // iter101 — auto-trigger Stripe Checkout when arriving from registration
  // (?welcome=1) or from the bulk-activation email (?activate=1). One-tap
  // path to the 14-day trial — no need to find and click the button.
  useEffect(() => {
    if (!user) return;
    if (isPro) return;
    if (!params.get("welcome") && !params.get("activate")) return;
    // Wait one tick so billing/me has time to resolve and the user can read the page first
    const t = setTimeout(() => { goPro(); }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isPro]);


  const benefits = [
    { Icon: MessageCircle, title: t("pro.benefit_ai_t"), body: t("pro.benefit_ai_b") },
    { Icon: Sparkles, title: t("pro.benefit_packing_t"), body: t("pro.benefit_packing_b") },
    { Icon: CloudRain, title: t("pro.benefit_weather_t"), body: t("pro.benefit_weather_b") },
    { Icon: Calendar, title: t("pro.benefit_dates_t"), body: t("pro.benefit_dates_b") },
    { Icon: Tag, title: t("pro.benefit_discounts_t"), body: t("pro.benefit_discounts_b") },
    { Icon: Camera, title: t("pro.benefit_photos_t"), body: t("pro.benefit_photos_b") },
    { Icon: MapPin, title: t("pro.benefit_outlook_t"), body: t("pro.benefit_outlook_b") },
    { Icon: Award, title: t("pro.benefit_badge_t"), body: t("pro.benefit_badge_b") },
    { Icon: Bell, title: t("pro.benefit_early_t"), body: t("pro.benefit_early_b") },
    { Icon: Users, title: t("pro.benefit_chat_t"), body: t("pro.benefit_chat_b") },
  ];

  const openPortal = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/billing/portal");
      if (data?.url) window.location.href = data.url;
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="pro-page">
      <Header active="pro" />
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-14">
        <div className="grid lg:grid-cols-[1.2fr_1fr] gap-12 items-start">
          <div>
            <div className="eyebrow mb-3 flex items-center gap-2">
              <Crown className="w-3 h-3 text-[#ff4500]" /> {t("pro.eyebrow")}
            </div>
            <h1 className="font-display text-5xl lg:text-6xl tracking-tight mb-6 leading-[0.95]">
              {t("pro.h1_a")}<br/>
              <span className="text-[#ff4500]">{t("pro.h1_b")}</span>
            </h1>
            <p className="text-lg text-[#333] leading-relaxed max-w-xl mb-8">
              {t("pro.intro")}
            </p>

            <div className="flex items-baseline gap-2 mb-2">
              <div className="font-display text-7xl mono leading-none">£1.99</div>
              <div className="text-lg text-[#555]">{t("pro.perMonth")}</div>
            </div>
            <div className="mb-1 text-[13px] text-[#222] max-w-md leading-snug" data-testid="pro-worth-line">
              Less than a flat white · funds the maps, the AI and a kind, ad-free community.
              <span className="text-[#ff4500]"> Worth every penny.</span>
            </div>
            <div className="eyebrow mb-6">{t("pro.trialEyebrow")}</div>

            {/* iter122 — "What you stand for" — anchors Pro in the shared
                values our community cares about (Mountain Rescue is the
                highest-profile one), without making any percentage-of-revenue
                claim. Pure values alignment, not a donation pledge. */}
            <div className="mb-8 border-l-2 border-[#16a34a] pl-4" data-testid="pro-values-card">
              <div className="text-[10px] mono uppercase tracking-[0.18em] text-[#16a34a] mb-1.5 flex items-center gap-1.5">
                <Crown className="w-3 h-3" /> What Pro members stand for
              </div>
              <p className="text-[13px] text-[#222] leading-relaxed max-w-md">
                Pro keeps CampCompass small, calm and ad-free — the kind of place where the people behind <Link to="/charities" className="text-[#16a34a] underline underline-offset-2 hover:text-[#0e0e0e]">Mountain Rescue</Link> get championed instead of buried under banner ads. Your sign-up makes you a quiet patron of that community.
              </p>
              <Link
                to="/charities"
                className="inline-flex items-center gap-1 mt-2 text-[11px] mono uppercase tracking-wider text-[#16a34a] hover:text-[#0e0e0e]"
                data-testid="pro-values-charities-link"
              >
                Meet the charities we back →
              </Link>
            </div>

            {isPro ? (
              <div className="p-5 border hairline-strong bg-[#fffdfa] flex flex-wrap items-center gap-4 max-w-lg" data-testid="pro-active">
                <Crown className="w-5 h-5 text-[#ff4500]" strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <div className="font-display text-xl">{t("pro.youreOnPro")}</div>
                  <div className="text-xs mono text-[#555]">
                    {t("pro.renewsAround", { date: billing.membership_expires_at?.slice(0, 10) })}
                  </div>
                </div>
                <button onClick={openPortal} disabled={busy}
                  className="btn-secondary inline-flex items-center gap-2 !px-4 !py-2 text-xs"
                  data-testid="manage-subscription-btn">
                  {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                  {t("pro.manageSubscription")}
                </button>
              </div>
            ) : (
              <div className="flex flex-wrap gap-3 items-center">
                <button onClick={goPro} disabled={busy}
                  className="btn-primary inline-flex items-center gap-2 !px-8 !py-4 text-base" data-testid="go-pro-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
                  {trialAvailable ? t("pro.startTrial") : t("pro.goProBtn")}
                </button>
              </div>
            )}
            <div className="mt-4 text-[11px] mono text-[#888] max-w-md leading-relaxed">
              {t("pro.smallPrint")}{" "}
              <Link to="/terms" className="underline">{t("pro.terms")}</Link>.
            </div>
          </div>

          <div className="space-y-0 border hairline bg-white" data-testid="pro-benefits">
            {benefits.map(({ Icon, title, body }, i) => (
              <div key={title} className={`p-5 flex gap-4 ${i > 0 ? "border-t hairline" : ""}`} data-testid={`pro-benefit-${i}`}>
                <Icon className="w-5 h-5 text-[#ff4500] shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <div className="font-display text-base mb-1 flex items-center gap-2">
                    {title} <Check className="w-3.5 h-3.5 text-[#ff4500]" strokeWidth={2.5} />
                  </div>
                  <p className="text-sm text-[#555] leading-relaxed">{body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-20 p-8 border hairline bg-white max-w-3xl">
          <div className="eyebrow mb-2">{t("pro.freeEyebrow")}</div>
          <div className="font-display text-2xl mb-3">{t("pro.freeTitle")}</div>
          <p className="text-sm text-[#555] leading-relaxed">
            {t("pro.freeBody")}
          </p>
        </div>
      </div>
      <Footer />
    </div>
  );
}
